import { useState, useEffect, useCallback } from "react";
import { crewOpsApi } from "../../api";
import { toast } from "sonner";
import {
  RefreshCw, CheckCircle, XCircle, Plane, Users, Clock, AlertTriangle,
  ChevronDown, ChevronUp, Rocket, FileText, Calendar,
} from "lucide-react";
import ManagementSignoff from "./ManagementSignoff";

const STATUS_META = {
  pending:  { label: "Pending Review", color: "text-amber-400",  bg: "bg-amber-500/10  border-amber-500/30"  },
  approved: { label: "Approved",       color: "text-teal-400",   bg: "bg-teal-500/10   border-teal-500/30"   },
  rejected: { label: "Rejected",       color: "text-red-400",    bg: "bg-red-500/10    border-red-500/30"    },
  deployed: { label: "Deployed",       color: "text-emerald-400",bg: "bg-emerald-500/10 border-emerald-500/30"},
};

const FDP_META = {
  normal:    { label: "Normal",    color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/25" },
  watch:     { label: "Watch",     color: "text-amber-400",   bg: "bg-amber-500/10   border-amber-500/25"   },
  violation: { label: "Violation", color: "text-red-400",     bg: "bg-red-500/10     border-red-500/25"     },
};

function CrewBadge({ person, role }) {
  if (!person?.name) return <span className="text-[9px] text-muted-foreground/40 italic">Not assigned</span>;
  const high = (person.fatigue_score || 0) > 65;
  return (
    <div className={`flex items-center gap-2 px-2 py-1.5 rounded-sm border text-[10px] ${high ? "bg-amber-500/10 border-amber-500/25" : "bg-white/5 border-border/30"}`}>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-foreground truncate">{person.name}</div>
        <div className="text-[8px] text-muted-foreground font-mono">{person.employee_id} · {role}</div>
      </div>
      <div className={`text-[9px] font-mono font-bold px-1.5 py-0.5 rounded-sm ${high ? "text-amber-400 bg-amber-500/15" : "text-emerald-400 bg-emerald-500/10"}`}>
        {person.fatigue_score ?? "—"}
      </div>
    </div>
  );
}

function SubmissionCard({ sub, onRefresh }) {
  const [expanded, setExpanded]     = useState(false);
  const [approving, setApproving]   = useState(false);
  const [rejecting, setRejecting]   = useState(false);
  const [deploying, setDeploying]   = useState(false);
  const [effDate, setEffDate]       = useState(() => new Date().toISOString().split("T")[0]);
  const [approver, setApprover]     = useState("Ops Manager");
  const [comments, setComments]     = useState("");
  const [showSignoff, setShowSignoff] = useState(false);

  const st  = STATUS_META[sub.status] || STATUS_META.pending;
  const fdp = FDP_META[sub.fdp_analysis?.status || "normal"];
  const ca  = sub.crew_assignments || {};
  const sch = sub.schedule || {};
  const allSectors = [...(sch.scheduled_sectors || []), ...(sch.extra_legs || [])];

  const handleApprove = async () => {
    setApproving(true);
    try {
      await crewOpsApi.approveSubmission(sub.id, { approved_by: approver, comments });
      toast.success("Submission approved");
      onRefresh();
    } catch { toast.error("Approval failed"); }
    finally { setApproving(false); }
  };

  const handleReject = async () => {
    setRejecting(true);
    try {
      await crewOpsApi.rejectSubmission(sub.id, { approved_by: approver, comments });
      toast.success("Submission rejected");
      onRefresh();
    } catch { toast.error("Reject failed"); }
    finally { setRejecting(false); }
  };

  const handleDeploy = async () => {
    if (!effDate) { toast.error("Please set an effective date"); return; }
    setDeploying(true);
    try {
      await crewOpsApi.deploySubmission(sub.id, { effective_date: effDate, deployed_by: approver });
      toast.success("Deployed to Crew Deployment");
      onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Deploy failed"); }
    finally { setDeploying(false); }
  };

  return (
    <div className="border border-border/50 rounded-sm bg-card/30 overflow-hidden" data-testid={`submission-card-${sub.id}`}>
      {/* Card header */}
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-white/[0.02] transition-colors"
        onClick={() => setExpanded(e => !e)}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="font-bold text-sm text-foreground truncate">{sub.scenario_name}</span>
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-sm border flex-shrink-0 ${st.bg} ${st.color}`}>{st.label}</span>
            {(sub.requires_dual_crew || ca.dual_crew) ? (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm border bg-red-500/10 border-red-500/30 text-red-300 flex-shrink-0 flex items-center gap-1">
                <AlertTriangle size={8} /> Dual Crew
              </span>
            ) : sub.crew_assignments?.fdp_watch && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm border bg-amber-500/10 border-amber-500/30 text-amber-400 flex-shrink-0 flex items-center gap-1">
                <AlertTriangle size={8} /> FDP Watch
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
            <span className="font-mono text-amber-400/80">{sub.tail_number}</span>
            <span>{sub.base}</span>
            <span>·</span>
            <span>{sch.month} {sch.date_day}</span>
            <span>·</span>
            {sub.requires_dual_crew || ca.dual_crew ? (
              <span className="font-bold text-red-400">
                Combined FDP {sub.fdp_analysis?.fdp_hours?.toFixed(1) ?? "—"}h → DUAL CREW
              </span>
            ) : (
              <span className={`font-bold ${fdp.color}`}>FDP {sub.fdp_analysis?.fdp_hours?.toFixed(1) ?? "—"}h / {sub.fdp_analysis?.dgca_limit}h</span>
            )}
            <span className="ml-auto opacity-60">{new Date(sub.submitted_at).toLocaleDateString("en-IN", { day:"2-digit", month:"short" })}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {sub.status === "deployed" && (
            <button
              onClick={e => { e.stopPropagation(); setShowSignoff(true); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold rounded-sm bg-indigo-500/15 border border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/25 transition-all"
              data-testid={`signoff-btn-${sub.id}`}>
              <FileText size={10} /> Mgmt Sign-off
            </button>
          )}
          {expanded ? <ChevronUp size={14} className="text-muted-foreground" /> : <ChevronDown size={14} className="text-muted-foreground" />}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-border/30">

          {/* FDP strip */}
          <div className={`flex items-center gap-3 px-3 py-2 rounded-sm border text-[10px] mt-3 ${fdp.bg} ${fdp.color}`}>
            <Clock size={12} />
            <span className="font-bold">FDP Analysis</span>
            <span>Report: <span className="font-mono font-bold">{sub.fdp_analysis?.report_time}</span></span>
            <span>→ Release: <span className="font-mono font-bold">{sub.fdp_analysis?.release_time}</span></span>
            <span>=<span className="font-mono font-bold ml-1">{sub.fdp_analysis?.fdp_hours?.toFixed(1)}h</span></span>
            <span className="ml-auto">DGCA limit: <span className="font-bold">{sub.fdp_analysis?.dgca_limit}h</span></span>
            <span className={`font-bold px-1.5 py-0.5 rounded-sm ${fdp.bg} border ${fdp.color}`}>{fdp.label.toUpperCase()}</span>
          </div>

          {/* Schedule sectors */}
          {allSectors.length > 0 && (
            <div>
              <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Full Schedule</div>
              <div className="flex flex-wrap gap-1.5">
                {(sch.scheduled_sectors || []).map((s, i) => (
                  <span key={`s${i}`} className="flex items-center gap-1 text-[10px] font-mono px-2 py-1 bg-amber-500/10 border border-amber-500/25 rounded-sm text-amber-300">
                    <Plane size={8} /> {s.dep}→{s.arr} <span className="text-amber-400/60">{s.depTime}–{s.arrTime}</span>
                  </span>
                ))}
                {(sch.extra_legs || []).map((s, i) => (
                  <span key={`e${i}`} className="flex items-center gap-1 text-[10px] font-mono px-2 py-1 bg-indigo-500/10 border border-indigo-500/25 rounded-sm text-indigo-300">
                    <Plane size={8} /> {s.dep}→{s.arr} <span className="text-indigo-400/60">{s.depTime}–{s.arrTime}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Crew assignments */}
          <div>
            <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground mb-2">AI-Selected Crew</div>
            {sub.requires_dual_crew || ca.dual_crew ? (
              <DualCrewPanel ca={ca} fdpPhase1={sub.fdp_phase1} fdpPhase2={sub.fdp_phase2} sch={sch} />
            ) : (
              <div className="space-y-1.5">
                <div className="grid grid-cols-2 gap-1.5">
                  <CrewBadge person={ca.captain}        role="Captain" />
                  <CrewBadge person={ca.first_officer}  role="First Officer" />
                  {(ca.cabin_crew || []).map((cc, i) => (
                    <CrewBadge key={i} person={cc} role={cc.role || `CC${i+1}`} />
                  ))}
                </div>
                {ca.ai_rationale && (
                  <div className="mt-2 px-3 py-2 bg-teal-500/5 border border-teal-500/15 rounded-sm text-[10px] text-teal-300/80 leading-relaxed">
                    {ca.ai_rationale}
                  </div>
                )}
                {ca.dgca_compliance_note && (
                  <div className="mt-1 text-[10px] text-muted-foreground/60 italic">{ca.dgca_compliance_note}</div>
                )}
              </div>
            )}
          </div>

          {/* Approval section */}
          {sub.approval && (
            <div className="px-3 py-2 bg-white/[0.03] border border-border/20 rounded-sm text-[10px] space-y-0.5">
              <div className="font-bold text-muted-foreground uppercase tracking-widest text-[8px] mb-1">Approval Record</div>
              <div className="text-foreground">{sub.approval.approved_by} · <span className="text-muted-foreground">{new Date(sub.approval.approved_at).toLocaleString("en-IN")}</span></div>
              {sub.approval.comments && <div className="text-muted-foreground italic">"{sub.approval.comments}"</div>}
            </div>
          )}

          {sub.deployment && (
            <div className="px-3 py-2 bg-emerald-500/5 border border-emerald-500/20 rounded-sm text-[10px] space-y-0.5">
              <div className="font-bold text-emerald-400 uppercase tracking-widest text-[8px] mb-1">Deployment Record</div>
              <div className="text-foreground">Effective Date: <span className="font-mono font-bold text-emerald-300">{sub.deployment.effective_date}</span></div>
              <div className="text-muted-foreground">By {sub.deployment.deployed_by} · {new Date(sub.deployment.deployed_at).toLocaleString("en-IN")}</div>
              {sub.deployment.notes && <div className="italic text-muted-foreground/60">"{sub.deployment.notes}"</div>}
            </div>
          )}

          {/* Action area */}
          {sub.status === "pending" && (
            <div className="space-y-2 pt-1">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[9px] text-muted-foreground block mb-0.5">Approver Name</label>
                  <input value={approver} onChange={e => setApprover(e.target.value)}
                    className="w-full bg-background border border-border rounded-sm px-2 py-1.5 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    data-testid="approver-name-input" />
                </div>
                <div>
                  <label className="text-[9px] text-muted-foreground block mb-0.5">Comments (optional)</label>
                  <input value={comments} onChange={e => setComments(e.target.value)}
                    className="w-full bg-background border border-border rounded-sm px-2 py-1.5 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={handleApprove} disabled={approving}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-bold rounded-sm bg-teal-500/15 border border-teal-500/30 text-teal-300 hover:bg-teal-500/25 disabled:opacity-40 transition-all"
                  data-testid={`approve-btn-${sub.id}`}>
                  {approving ? <RefreshCw size={11} className="animate-spin" /> : <CheckCircle size={11} />}
                  {approving ? "Approving…" : "Approve"}
                </button>
                <button onClick={handleReject} disabled={rejecting}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-bold rounded-sm bg-red-500/10 border border-red-500/25 text-red-400 hover:bg-red-500/15 disabled:opacity-40 transition-all"
                  data-testid={`reject-btn-${sub.id}`}>
                  {rejecting ? <RefreshCw size={11} className="animate-spin" /> : <XCircle size={11} />}
                  {rejecting ? "Rejecting…" : "Reject"}
                </button>
              </div>
            </div>
          )}

          {sub.status === "approved" && (
            <div className="space-y-2 pt-1">
              <div className="text-[9px] font-bold uppercase tracking-widest text-teal-400">Deploy Scenario</div>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <label className="text-[9px] text-muted-foreground block mb-0.5 flex items-center gap-1"><Calendar size={9} /> Effective Date</label>
                  <input type="date" value={effDate} onChange={e => setEffDate(e.target.value)}
                    className="w-full bg-background border border-border rounded-sm px-2 py-1.5 text-xs font-mono text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    data-testid={`effective-date-input-${sub.id}`} />
                </div>
                <button onClick={handleDeploy} disabled={deploying}
                  className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-sm bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 disabled:opacity-40 transition-all"
                  data-testid={`deploy-btn-${sub.id}`}>
                  {deploying ? <RefreshCw size={11} className="animate-spin" /> : <Rocket size={11} />}
                  {deploying ? "Deploying…" : "Deploy"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {showSignoff && <ManagementSignoff submission={sub} onClose={() => setShowSignoff(false)} />}
    </div>
  );
}

// Dual-crew panel: shows two separate crew + FDP blocks side by side
function DualCrewPanel({ ca, fdpPhase1, fdpPhase2, sch }) {
  const f1 = fdpPhase1 || {};
  const f2 = fdpPhase2 || {};
  const crew1 = ca.crew_1 || {};
  const crew2 = ca.crew_2 || {};

  return (
    <div className="space-y-3">
      {/* DGCA violation notice */}
      <div className="flex items-start gap-2 px-3 py-2.5 rounded-sm border border-red-500/30 bg-red-500/10" data-testid="dual-crew-violation-banner">
        <AlertTriangle size={12} className="text-red-400 mt-0.5 flex-shrink-0" />
        <div>
          <div className="text-[10px] font-bold text-red-300">DGCA FDP Violation — Dual Crew Required</div>
          <div className="text-[9px] text-red-300/70 mt-0.5">
            {ca.dual_crew_reason || "Combined FDP exceeds DGCA 13h limit. Two separate crews must operate Phase 1 and Phase 2."}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {/* Crew 1 — Phase 1 */}
        <div className="border border-amber-500/25 rounded-sm bg-amber-500/5 p-3 space-y-2" data-testid="crew-1-panel">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[9px] font-black uppercase tracking-widest text-amber-400">Crew 1 — Phase 1</span>
            <span className="text-[8px] font-mono text-amber-400/60">({sch.scheduled_sectors?.length || 0} sector{(sch.scheduled_sectors?.length || 0) !== 1 ? "s" : ""})</span>
          </div>
          {/* FDP strip */}
          <div className={`flex items-center gap-2 px-2 py-1.5 rounded-sm border text-[9px] ${FDP_META[f1.status || "normal"].bg} ${FDP_META[f1.status || "normal"].color}`}>
            <Clock size={9} />
            <span className="font-mono font-bold">{f1.report_time}</span>
            <span className="opacity-60">→</span>
            <span className="font-mono font-bold">{f1.release_time}</span>
            <span className="ml-auto font-bold">{f1.fdp_hours?.toFixed(1)}h / {f1.dgca_limit}h</span>
          </div>
          <div className="space-y-1">
            <CrewBadge person={crew1.captain}       role="Captain" />
            <CrewBadge person={crew1.first_officer} role="First Officer" />
            {(crew1.cabin_crew || []).map((cc, i) => <CrewBadge key={i} person={cc} role={cc.role || `CC${i+1}`} />)}
          </div>
          {crew1.ai_rationale && (
            <div className="text-[9px] text-amber-300/60 italic leading-relaxed border-t border-amber-500/15 pt-1.5">
              {crew1.ai_rationale}
            </div>
          )}
        </div>

        {/* Crew 2 — Phase 2 */}
        <div className="border border-indigo-500/25 rounded-sm bg-indigo-500/5 p-3 space-y-2" data-testid="crew-2-panel">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[9px] font-black uppercase tracking-widest text-indigo-400">Crew 2 — Phase 2</span>
            <span className="text-[8px] font-mono text-indigo-400/60">({sch.extra_legs?.length || 0} extra leg{(sch.extra_legs?.length || 0) !== 1 ? "s" : ""})</span>
          </div>
          {/* FDP strip */}
          <div className={`flex items-center gap-2 px-2 py-1.5 rounded-sm border text-[9px] ${FDP_META[f2.status || "normal"].bg} ${FDP_META[f2.status || "normal"].color}`}>
            <Clock size={9} />
            <span className="font-mono font-bold">{f2.report_time}</span>
            <span className="opacity-60">→</span>
            <span className="font-mono font-bold">{f2.release_time}</span>
            <span className="ml-auto font-bold">{f2.fdp_hours?.toFixed(1)}h / {f2.dgca_limit}h</span>
          </div>
          <div className="space-y-1">
            <CrewBadge person={crew2.captain}       role="Captain" />
            <CrewBadge person={crew2.first_officer} role="First Officer" />
            {(crew2.cabin_crew || []).map((cc, i) => <CrewBadge key={i} person={cc} role={cc.role || `CC${i+1}`} />)}
          </div>
          {crew2.ai_rationale && (
            <div className="text-[9px] text-indigo-300/60 italic leading-relaxed border-t border-indigo-500/15 pt-1.5">
              {crew2.ai_rationale}
            </div>
          )}
        </div>
      </div>

      {ca.dgca_compliance_note && (
        <div className="text-[9px] text-emerald-300/70 italic px-2">{ca.dgca_compliance_note}</div>
      )}
    </div>
  );
}


export default function ScenarioRostering() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const r = await crewOpsApi.listSubmissions(params);
      setSubmissions(r.data || []);
    } catch { toast.error("Failed to load submissions"); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const counts = submissions.reduce((acc, s) => {
    acc[s.status] = (acc[s.status] || 0) + 1; return acc;
  }, {});

  return (
    <div className="flex-1 overflow-y-auto p-6" data-testid="scenario-rostering-tab">
      <div className="max-w-4xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-sm font-black uppercase tracking-widest text-foreground mb-1">Crew Rostering — Scenario Submissions</h3>
            <p className="text-[11px] text-muted-foreground">
              AI-selected crew + FDP analysis → Ops Manager approval → Deployment with effective date
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="bg-card border border-border rounded-sm px-2 py-1 text-xs text-foreground focus:outline-none"
              data-testid="submissions-status-filter">
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="deployed">Deployed</option>
            </select>
            <button onClick={load} disabled={loading}
              className="p-1.5 border border-border/40 rounded-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
              data-testid="refresh-submissions-btn">
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>

        {/* Status counters */}
        {submissions.length > 0 && (
          <div className="flex gap-3">
            {[
              ["pending",  counts.pending  || 0, "text-amber-400",   "bg-amber-500/10  border-amber-500/25"],
              ["approved", counts.approved || 0, "text-teal-400",    "bg-teal-500/10   border-teal-500/25"],
              ["deployed", counts.deployed || 0, "text-emerald-400", "bg-emerald-500/10 border-emerald-500/25"],
              ["rejected", counts.rejected || 0, "text-red-400",     "bg-red-500/10    border-red-500/25"],
            ].map(([s, n, c, bg]) => (
              <button key={s} onClick={() => setStatusFilter(statusFilter === s ? "" : s)}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-sm border text-[10px] font-bold transition-all ${bg} ${c} ${statusFilter === s ? "ring-1 ring-current" : "opacity-70 hover:opacity-100"}`}>
                <span className="font-mono">{n}</span> {s}
              </button>
            ))}
          </div>
        )}

        {/* Workflow info banner */}
        <div className="flex items-stretch gap-0 overflow-hidden rounded-sm border border-border/30">
          {[
            { icon: FileText, label: "1. Submit",   desc: "From Scenarios tab", color: "text-amber-400",   bg: "bg-amber-500/5" },
            { icon: Users,    label: "2. AI Crew",  desc: "Auto-selected + FDP", color: "text-indigo-400",  bg: "bg-indigo-500/5" },
            { icon: CheckCircle, label: "3. Approve", desc: "Ops Manager review", color: "text-teal-400",    bg: "bg-teal-500/5" },
            { icon: Rocket,   label: "4. Deploy",   desc: "Effective date set",  color: "text-emerald-400", bg: "bg-emerald-500/5" },
          ].map(({ icon: Icon, label, desc, color, bg }, i) => (
            <div key={i} className={`flex-1 flex flex-col items-center py-3 px-2 ${bg} border-r border-border/20 last:border-r-0`}>
              <Icon size={14} className={`${color} mb-1`} />
              <div className={`text-[9px] font-bold ${color}`}>{label}</div>
              <div className="text-[8px] text-muted-foreground/60 text-center">{desc}</div>
            </div>
          ))}
        </div>

        {/* Submissions list */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground text-sm animate-pulse">Loading submissions...</div>
        ) : submissions.length === 0 ? (
          <div className="text-center py-16 space-y-3 border border-border/20 rounded-sm bg-white/[0.02]">
            <Users size={32} className="text-muted-foreground/20 mx-auto" />
            <div className="text-sm text-muted-foreground">No submissions yet</div>
            <div className="text-[11px] text-muted-foreground/50 max-w-xs mx-auto">
              Go to the Scenarios tab → find a saved scenario → click "Submit to Rostering".
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {submissions.map(s => (
              <SubmissionCard key={s.id} sub={s} onRefresh={load} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
